import { z } from 'zod';
import type { SalesOrderDto } from '../types/salesOrder.types';

// RHF form schema for the SO Order Details workspace form (86d3afyp6).
//
// The LIVE-EDIT shape — every editable field of the Order Details form. RHF owns these
// so text inputs are uncontrolled (no per-keystroke panel re-render); selects/checkboxes
// use Controller for a single source of truth.
//
// Deliberately EXCLUDED (the watch-sync golden rule — never patched, never clobbered):
//   - lineItems (store/table-managed),
//   - the pricing-carry-over snapshot (grade*Markup, pricePerKg*, overallPricePerKg,
//     totalAuthorizedKg, perGradeBalanceKgJson),
//   - FO-snapshot / identity fields (linkedFirmOffer*, customerId, customerName,
//     customerEntityId, headMainOffice*, salesRepId, salesCoordId,
//     contractExpiryDate, status, id, salesOrderNumber, date, netSuite*, posted*,
//     prepared*, extraction*, timestamps),
//   - subagentId / reasonForClosing (no input in the workspace bodies).
//
// `formDataToPatch` returns ONLY these keys, so the 200ms watch-sync never patches a
// non-form field. Fields written outside RHF (FO pick, company cascade) re-sync the form
// via reset/setValue. Submit-time validation stays in salesOrderSchema.ts.

// NOTE: grown incrementally as each body is converted to RHF, so every intermediate
// state stays coherent (the form owns exactly the fields whose inputs are RHF-wired;
// everything else keeps the store path until its body is converted). 86d3afyp6.
export const SALES_ORDER_FORM_KEYS = [
  // Customer & Contract (editable)
  'doCustCompanyId',
  'doCustCompanyName',
  'projectName',
  'termsId',
  'termsName',
  // Purchase Order & Documents
  'customerPoNumber',
  'poExpiryDate',
  'waitingForOfficialPo',
  'subPoOrderlistLink',
  'invoiceToLetter',
  // Freight
  'freightChargePerTon', // 86d3fteqa: editable rate that drives the charge
  'actualFreightPerMt',
  'waivedFreight',
  'isDiscountedFreight',
  'discountDetails',
  'separateBilling',
  'includeFreightLine',
  // Shipping (flat fields)
  'shippingAddressId',
  'shippingAddresseeName',
  'shippingAddrLine1',
  'shippingAddrLine2',
  'shippingAddrLine3',
  'shippingCityTownId',
  'shippingStateProvinceId',
  'shippingZip',
  'shippingCountryId',
  'shippingAddressOverrideJson',
  // Billing (flat fields)
  'billingAddressId',
  'billingAddresseeName',
  'billingAddrLine1',
  'billingAddrLine2',
  'billingAddrLine3',
  'billingCityTownId',
  'billingStateProvinceId',
  'billingZip',
  'billingCountryId',
  'billingAddressOverrideJson',
  // Delivery Requirements
  'deliveryDueDate',
  'scheduleId',
  'scheduleName',
  'authorizedReceivers',
  'otherInstructions',
  'officeContact1',
  'contactNumber1',
  'officeContact2',
  'contactNumber2',
  'officeContact3',
  'contactNumber3',
  // Document Alerts
  'alertInvoiceECopy',
  'alertManualComputation',
  'alertMc2Copies',
  'alertDrPicture',
  'alertDrAttachment',
  'alertBrnTraceability',
  // Flags + remarks
  'isTrailer',
  'isMf',
  'isPpeRequired',
  'isPermitRequired',
  'isPriority',
  'isSpotOrder',
  'isReprint',
  'soRemarks',
] as const satisfies readonly (keyof SalesOrderDto)[];

export type SalesOrderFormData = Pick<SalesOrderDto, (typeof SALES_ORDER_FORM_KEYS)[number]>;

const nullableStr = z.string().nullable();
export const salesOrderFormSchema = z.object({
  doCustCompanyId: nullableStr,
  doCustCompanyName: nullableStr,
  // Required at submit (86d3f81cz) — non-nullable in the DTO. Mirrors waivedFreight's
  // plain z.string(); the required gate lives in sectionStatus + the submit validator,
  // not here (same as customerPoNumber).
  projectName: z.string(),
  termsId: nullableStr,
  termsName: nullableStr,
  customerPoNumber: nullableStr,
  poExpiryDate: nullableStr,
  waitingForOfficialPo: z.boolean(),
  subPoOrderlistLink: nullableStr,
  invoiceToLetter: nullableStr,
  freightChargePerTon: z.number().nullable(),
  actualFreightPerMt: z.number().nullable(),
  waivedFreight: z.string(),
  isDiscountedFreight: z.boolean(),
  discountDetails: nullableStr,
  separateBilling: z.string(),
  includeFreightLine: z.boolean().nullable(),
  shippingAddressId: nullableStr,
  shippingAddresseeName: nullableStr,
  shippingAddrLine1: nullableStr,
  shippingAddrLine2: nullableStr,
  shippingAddrLine3: nullableStr,
  shippingCityTownId: nullableStr,
  shippingStateProvinceId: nullableStr,
  shippingZip: nullableStr,
  shippingCountryId: nullableStr,
  shippingAddressOverrideJson: nullableStr,
  billingAddressId: nullableStr,
  billingAddresseeName: nullableStr,
  billingAddrLine1: nullableStr,
  billingAddrLine2: nullableStr,
  billingAddrLine3: nullableStr,
  billingCityTownId: nullableStr,
  billingStateProvinceId: nullableStr,
  billingZip: nullableStr,
  billingCountryId: nullableStr,
  billingAddressOverrideJson: nullableStr,
  deliveryDueDate: nullableStr,
  scheduleId: nullableStr,
  scheduleName: nullableStr,
  authorizedReceivers: nullableStr,
  otherInstructions: nullableStr,
  officeContact1: nullableStr,
  contactNumber1: nullableStr,
  officeContact2: nullableStr,
  contactNumber2: nullableStr,
  officeContact3: nullableStr,
  contactNumber3: nullableStr,
  alertInvoiceECopy: z.boolean(),
  alertManualComputation: z.boolean(),
  alertMc2Copies: z.boolean(),
  alertDrPicture: z.boolean(),
  alertDrAttachment: z.boolean(),
  alertBrnTraceability: z.boolean(),
  isTrailer: z.boolean(),
  isMf: z.boolean(),
  isPpeRequired: z.boolean(),
  isPermitRequired: z.boolean(),
  isPriority: z.boolean(),
  isSpotOrder: z.boolean(),
  isReprint: z.boolean(),
  soRemarks: nullableStr,
}) satisfies z.ZodType<SalesOrderFormData>;

/** Project the store draft into the form's editable shape (defaultValues / reset). */
export function draftToFormData(draft: SalesOrderDto): SalesOrderFormData {
  const out = {} as Record<string, unknown>;
  for (const key of SALES_ORDER_FORM_KEYS) {
    out[key] = draft[key];
  }
  return out as SalesOrderFormData;
}

/** Convert form values into a draft patch — form-field keys ONLY. Empty strings
 *  normalize to null to preserve the draft's `string | null` semantics. */
export function formDataToPatch(values: SalesOrderFormData): Partial<SalesOrderDto> {
  const patch = {} as Record<string, unknown>;
  for (const key of SALES_ORDER_FORM_KEYS) {
    const v = values[key];
    patch[key] = v === '' ? null : v;
  }
  return patch as Partial<SalesOrderDto>;
}
